/**
 * Lazy Echo (Pusher) initializer.
 * Call `initEcho()` to set up the WebSocket connection.
 * Echo and Pusher are dynamically imported to keep the main bundle small.
 */

let _initialized = false;

export async function initEcho() {
  // Only set up once per session
  if (_initialized && window.Echo) return window.Echo;
  _initialized = true;

  // Force complete cleanup of any existing connections
  if (window.Echo) {
    try { window.Echo.disconnect(); } catch (e) {}
  }
  if (window.Pusher) {
    try {
      Object.keys(window.Pusher.instances || {}).forEach(key => {
        window.Pusher.instances[key].disconnect();
      });
    } catch (e) {}
  }
  delete window.Echo;
  delete window.Pusher;

  // Dynamic imports — only loaded when user is actually authenticated
  const [{ default: Echo }, { default: Pusher }] = await Promise.all([
    import('laravel-echo'),
    import('pusher-js'),
  ]);

  // Fresh setup
  window.Pusher = Pusher;

  window.Echo = new Echo({
    broadcaster: 'pusher',
    key: process.env.REACT_APP_PUSHER_APP_KEY,
    cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER,
    forceTLS: true,
    encrypted: true,
    authorizer: (channel) => {
      return {
        authorize: (socketId, callback) => {
          const token = localStorage.getItem("token");
          fetch(`${process.env.REACT_APP_API_URL}/broadcasting/auth`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
            body: JSON.stringify({
              socket_id: socketId,
              channel_name: channel.name,
            }),
          })
          .then(response => {
            if (!response.ok) throw new Error('Auth failed');
            return response.json();
          })
          .then(data => callback(null, data))
          .catch(error => callback(error));
        },
      };
    },
  });

  return window.Echo;
}

/**
 * Disconnect and clean up Echo/Pusher.
 * Call on logout for clean teardown.
 */
export function disconnectEcho() {
  if (window.Echo) {
    try { window.Echo.disconnect(); } catch (e) {}
  }
  delete window.Echo;
  delete window.Pusher;
  _initialized = false;
}
